import type { ReactNode } from "react"
import cx from "clsx"
import { Link } from "wouter"
import type { TemplateModule } from "shared"
import { ModuleLibrary } from "./ModuleLibrary"
import { VersionTag } from "@rawa/ui/VersionTag"
import { useOffCanvas } from "../../../dragAndDrop/offCanvas"
import { useCanvas } from "../../../dragAndDrop/useCanvas"
import {
  isLibraryModule,
  isModule,
  isPlacedModule,
  templateCanvas,
} from "../utils/templateDrag"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import { paths } from "../../../paths"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./SessionTemplateBuilder.module.css"

/**
 * The session template builder's shell: the title, the details panel, the
 * canvas with its action row, and the module library beside it. The screen
 * that creates a template and the one that edits it fill the panels
 * differently and share nothing else, so the layout is what they share.
 */
export function SessionTemplateBuilder({
  name,
  version,
  headerAction,
  notice,
  details,
  children,
  actions,
  onAddModule,
  placedModuleIds,
  unsaved = false,
}: {
  /** The template's name, or as much of it as has been typed. */
  name: string
  /** The stored template's version; a template not yet saved has none. */
  version?: number
  /** Beside the title, as the module canvas puts its Preview. */
  headerAction?: ReactNode
  notice?: ReactNode
  details: ReactNode
  /** What the canvas holds. */
  children: ReactNode
  actions: ReactNode
  /** Places a module on the template; absent while there is no template to hold one. */
  onAddModule?: (module: TemplateModule) => void
  /** The modules already on the template, which the library stops offering. */
  placedModuleIds?: string[]
  /** Whether what is on screen differs from what is saved. */
  unsaved?: boolean
}) {
  const canvas = useCanvas<HTMLElement>({
    zone: templateCanvas,
    accepts: isModule,
    lightsFor: isLibraryModule,
  })
  useOffCanvas(isPlacedModule)

  return (
    <div className={styles.page}>
      <Link className={styles.backLink} href={paths.sessionTemplates}>
        Back to session templates
      </Link>

      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Session template builder</p>
          <div className={styles.nameRow}>
            <h1 className={cx(typography.h2, styles.heading)}>
              {name || "New template"}
            </h1>
            {version !== undefined && <VersionTag version={version} />}
          </div>
          <p className={styles.subtitle}>
            Build the template from existing modules and set how each one
            behaves.
          </p>
        </div>
        {headerAction}
      </div>

      {notice}

      <div className={styles.builder}>
        <aside className={styles.details}>
          <PanelEyebrow>Template details</PanelEyebrow>
          {details}
        </aside>

        <section
          ref={canvas.ref}
          aria-label="Template canvas"
          className={styles.canvas}
          data-drop-over={canvas.over || undefined}
        >
          <div className={styles.canvasTop}>
            <div>
              <PanelEyebrow>Template canvas</PanelEyebrow>
              <h2 className={cx(typography.h3, styles.canvasHeading)}>
                {name || "Untitled template"}
              </h2>
            </div>
            {unsaved && (
              <span className={styles.unsavedBadge}>Unsaved changes</span>
            )}
          </div>

          {children}

          <div className={styles.actions}>{actions}</div>
        </section>

        <ModuleLibrary onAdd={onAddModule} placedIds={placedModuleIds} />
      </div>
    </div>
  )
}

/** A panel's section heading. */
function PanelEyebrow({ children }: { children: ReactNode }) {
  return <p className={styles.panelEyebrow}>{children}</p>
}

/** The canvas before any module is on it, with what to do about that. */
export function EmptyCanvas({ children }: { children: ReactNode }) {
  return (
    <div className={styles.emptyState}>
      <LayersIcon aria-hidden="true" />
      <h3>No modules added</h3>
      <p>{children}</p>
    </div>
  )
}
